<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useForm } from 'vee-validate'
import { onMounted, ref, watch } from 'vue'

import type { BankAccount } from '@/types/external/2work/api/request/BankAccount.ts'
import type { FrappeInterceptNewCreateMsgPayload } from '@/utilities/handleFrappeInterceptNewCreateActions'

import ButtonPrimary from '@/components/common/ui/buttons/ButtonPrimary.vue'
import SectionBankAccountOrganization from '@/components/editRelationGroup/sections/SectionBankAccountOrganization.vue'
import { useZodSchemaStore } from '@/components/editRelationGroup/sectionSelfUpdates/zodSchemaStore'
import { useBankOptions } from '@/composables/useBankOptions'
import { useNetworkError } from '@/composables/useNetworkError'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI'
import { useBankAccountStore } from '@/stores/bankAccountStore.ts'
import { useSimpleNotificationStore } from '@/stores/simpleNotificationStore'
import { openBankAccountPaymentCodeBlockAlert } from '@/utilities/bankAccountPaymentCodeGuard'
import {
  type BankFormValues,
  buildBankAccountPayload,
  toBankFormValuesFromAccount,
} from '@/utilities/bankHelpers'
import { translationFunctionDefault } from '@/utilities/translations'

const { frappeInterceptNewCreateMsgPayload } = defineProps<{
  frappeInterceptNewCreateMsgPayload: FrappeInterceptNewCreateMsgPayload
}>()

// translations
const tfDefault = translationFunctionDefault()

// stores
const simpleNotificationStore = useSimpleNotificationStore()
const frappeCustomAPI = useFrappeNestCustomAPIStore()
const zodSchema = useZodSchemaStore()
const bankAccountStore = useBankAccountStore()

// refs
const bankOptionsState = useBankOptions()
const { bankOptions } = bankOptionsState
const { bankAccountRefineMent, relationSchemas } = zodSchema

const {
  organizationSchemas: { bankAccountSchema },
} = relationSchemas

const { paymentCodeEnabled, targetName } = frappeInterceptNewCreateMsgPayload

// Tracks whether the account being edited was the company default on load, so we
// can hard-block unsetting that default while the payment QR code feature is on.
const wasDefaultBankAccount = ref(false)

const validationSchema = toTypedSchema(bankAccountSchema.superRefine(bankAccountRefineMent))
const { handleSubmit, setFieldError, setValues, values } = useForm<BankFormValues>({
  validationSchema,
})

// error handling
const onError = (): void => {
  simpleNotificationStore.triggerSimpleNotificationError(
    tfDefault('editRelationGroup.editError', { fieldName: tfDefault('fieldNames.bankAccount') }),
  )
}
const handleNetworkError = useNetworkError(onError)

// Resolve the party info used to build the bank account payload from the parent doctype.
const resolveParty = (): null | { party: string; partyType?: string } => {
  const parent = frappeInterceptNewCreateMsgPayload.parentDoctype

  if (!parent) {
    return null
  }
  // Determine partyType
  let partyType: string | undefined = undefined
  if (parent.party_type) {
    partyType = parent.party_type
  } else if (parent.customer) {
    partyType = 'Customer'
  } else if (parent.supplier) {
    partyType = 'Supplier'
  }

  // Collect possible party identifiers in priority order and pick the first non-empty string.
  const partyCandidates = [
    parent.party,
    parent.party_name,
    parent.customer,
    parent.supplier,
    parent.company,
  ]
  const party = partyCandidates.find((value) => typeof value === 'string' && value.trim() !== '')

  // If no party could be resolved, return null so callers can handle the error.
  if (!party) {
    return null
  }

  // Return resolved party data for payload creation.
  if (partyType) {
    return { party, partyType }
  }
  return { party }
}

const bankAccountCallWrapper = async (promise: Promise<BankAccount>): Promise<void> => {
  await promise
    .then(() => {
      bankAccountStore.after()
    })
    .catch((networkError: unknown) => {
      bankAccountStore.onNetworkError(networkError)
      if (bankAccountStore.ibanBackendErrorMessage) {
        setFieldError('tw_bank_account_iban', bankAccountStore.ibanBackendErrorMessage)
      }
    })
}

const createCompanyBankAccountTiggerList = ['bank_account', 'create_bank_account']
const updateCompanyBankAccountTiggerList = ['update_bank_account']

const onSubmit = async (values: BankFormValues): Promise<void> => {
  // Hard-block unsetting the company's default bank account while the payment QR
  // code feature is enabled (the QR code on print formats relies on it).
  if (paymentCodeEnabled && wasDefaultBankAccount.value && values.is_default !== 1) {
    openBankAccountPaymentCodeBlockAlert()
    return
  }

  bankAccountStore.ibanBackendErrorMessage = undefined
  const resolved = resolveParty()
  try {
    const bankAccountPayload = buildBankAccountPayload(
      values,
      resolved?.party ?? '',
      resolved?.partyType,
    )
    if (!resolved) {
      delete bankAccountPayload.party
      delete bankAccountPayload.party_type
    }
    if (
      !createCompanyBankAccountTiggerList.includes(
        frappeInterceptNewCreateMsgPayload.clickedFieldName,
      ) &&
      !updateCompanyBankAccountTiggerList.includes(
        frappeInterceptNewCreateMsgPayload.clickedFieldName,
      )
    ) {
      bankAccountPayload.is_company_account = 0
    }

    if (
      targetName &&
      updateCompanyBankAccountTiggerList.includes(
        frappeInterceptNewCreateMsgPayload.clickedFieldName,
      )
    ) {
      await bankAccountCallWrapper(
        bankAccountStore.updateBankAccountHandler(targetName, bankAccountPayload),
      )
    } else {
      await bankAccountCallWrapper(bankAccountStore.createBankAccountHandler(bankAccountPayload))
    }
  } catch (networkError) {
    console.error('network error:', networkError)
    await handleNetworkError(networkError)
  }
}

const onSubmitHandler = handleSubmit(async (values) => {
  await onSubmit(values)
})

const prefillBankAccountValues = async (): Promise<void> => {
  if (
    !updateCompanyBankAccountTiggerList.includes(
      frappeInterceptNewCreateMsgPayload.clickedFieldName,
    )
  ) {
    return
  }

  if (!targetName) {
    return
  }

  try {
    const [bankAccount] = await frappeCustomAPI.getBankAccountList(undefined, [
      ['name', '=', targetName],
    ])
    if (!bankAccount) {
      return
    }

    wasDefaultBankAccount.value = bankAccount.is_default === 1
    setValues(toBankFormValuesFromAccount(bankAccount))
  } catch (errorMsg) {
    console.error('getBankAccountList error', errorMsg)
    await handleNetworkError(errorMsg)
  }
}

// hooks
onMounted(() => {
  bankOptionsState.loadBankOptions().catch((errorMsg: unknown) => {
    console.error('loadBankOptions error', errorMsg)
  })
  prefillBankAccountValues().catch((errorMsg: unknown) => {
    console.error('prefillBankAccountValues error', errorMsg)
  })
})

watch(
  () => values.tw_bank_account_iban,
  () => {
    if (!bankAccountStore.ibanBackendErrorMessage) return
    bankAccountStore.ibanBackendErrorMessage = undefined
    setFieldError('tw_bank_account_iban', undefined)
  },
)
</script>

<template>
  <form @submit="onSubmitHandler">
    <div class="space-y-4 pt-4">
      <SectionBankAccountOrganization
        :bank-options="bankOptions"
        compact-padding
        :iban-error-message="bankAccountStore.ibanBackendErrorMessage"
        :is-collapsible="false"
        :show-title="false"
        :with-primary="
          createCompanyBankAccountTiggerList.includes(
            frappeInterceptNewCreateMsgPayload.clickedFieldName,
          ) ||
          updateCompanyBankAccountTiggerList.includes(
            frappeInterceptNewCreateMsgPayload.clickedFieldName,
          )
        "
      />
      <div class="flex justify-end">
        <ButtonPrimary :label="tfDefault('editOrganization.saveBankAccount')" type="submit" />
      </div>
    </div>
  </form>
</template>
